import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotices } from "../hooks/useNotices";
import React, { useEffect, useState } from "react";
import { NoticesHeader } from "../components/notices/noticesHeader";
import { LocalizedDateTime } from "../components/common/localizedDateTime";
import { Notice } from "../models/notice";
import { NoticeEditorComp } from "../components/notices/noticeEditorComp";
import { BottomModal } from "../components/common/BottomModal";
import { useAtom, useAtomValue } from "jotai";
import { isBackstageUserAtom, userAtom } from "../atoms/userAtom";
import { LoginType } from "../models/enum/login";
import { Button } from "../components/common/button";
import { editingNoticeAtom } from "../atoms/editingNoticeAtom";
import { NoticeImageCarousel } from "../components/notices/noticeImageCarousel";

export function Notices() {
  const { t } = useTranslation();
  const { artistId, eventId, rewardId, noticeId } = useParams<{
    artistId: string;
    eventId: string;
    rewardId: string;
    noticeId?: string;
  }>();

  const artistIdNum = parseInt(artistId!, 10);
  const eventIdNum = parseInt(eventId!, 10);
  const rewardIdNum = parseInt(rewardId!, 10);
  const noticeIdNum = noticeId ? parseInt(noticeId, 10) : undefined;
  const navigate = useNavigate();
  const { notices, isLoading, isError, error, refetch } = useNotices(
    artistIdNum,
    eventIdNum,
    rewardIdNum,
  );
  const [expandedNoticeId, setExpandedNoticeId] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const admin = useAtomValue(userAtom);
  const isBackstageAdmin = useAtomValue(isBackstageUserAtom);
  const basePath = `/artists/${artistId}/events/${eventId}/rewards/${rewardId}/notices`;
  const [editingNotice, setEditingNotice] = useAtom(editingNoticeAtom);

  useEffect(() => {
    if (notices && notices.length > 0) {
      if (noticeIdNum && notices.some((n) => n.id === noticeIdNum)) {
        setExpandedNoticeId(noticeIdNum);
      } else if (!noticeIdNum) {
        setExpandedNoticeId(null);
      } else {
        setExpandedNoticeId(null);
        navigate(basePath, { replace: true });
      }
    } else {
      setExpandedNoticeId(null);
    }
  }, [notices, noticeIdNum, navigate, basePath]);

  const handleNoticeClick = (id: number) => {
    const newExpandedNoticeId = id === expandedNoticeId ? null : id;
    setExpandedNoticeId(newExpandedNoticeId);

    if (newExpandedNoticeId) {
      navigate(`${basePath}/${newExpandedNoticeId}`, { replace: true });
    } else {
      navigate(`${basePath}`, { replace: true });
    }
  };

  const editNotice = (notice: Notice, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingNotice(notice);
    if (isBackstageAdmin) {
      navigate(
        `/artists/${artistId}/events/${eventId}/rewards/${rewardId}/notices/${notice.id}/noticeForm`,
      );
    } else {
      setShowEditor(true);
    }
  };

  const onEditorClosed = () => {
    setShowEditor(false);
    setEditingNotice(null);
    refetch().then();
  };

  return (
    <div className={"flex flex-col h-full"}>
      <NoticesHeader title={t("eventdetail_notice")} />
      <div className="p-4 flex-grow overflow-y-auto">
        {isLoading && (
          <div className={"flex justify-center items-center"}>
            <span className={"loading loading-spinner loading-lg"}></span>
          </div>
        )}
        {isError && (
          <div className="text-center text-error mt-8">
            <p>
              {t(
                "notices_error_loading",
                "공지사항을 불러오는 데 실패했습니다.",
              )}
            </p>
            {error?.message && <p className="text-sm">{error.message}</p>}
          </div>
        )}
        {!isLoading && !isError && (!notices || notices.length === 0) && (
          <div className="text-center text-gray-500 mt-8">
            <p>{t("eventdetail_notice_emptystate")}</p>
          </div>
        )}
        {!isLoading && !isError && notices && notices.length > 0 && (
          <div className={"space-y-4"}>
            {notices.map((notice) => (
              <div
                key={notice.id}
                className={`drop-shadow collapse bg-base-100 rounded-md ${
                  expandedNoticeId === notice.id
                    ? "collapse-open"
                    : "collapse-close"
                }`}
              >
                <div
                  className={
                    "collapse-title text-base font-medium flex flex-col items-start gap-1 p-4 pb-2"
                  }
                >
                  <div className={"flex justify-between w-full items-center"}>
                    {notice.updatedAt && (
                      <p className={"text-xs text-gray-500 mb-1"}>
                        <LocalizedDateTime dateTime={notice.updatedAt} />
                      </p>
                    )}
                    <button
                      className={`btn btn-outline btn-xs ${admin?.source === LoginType.ONE_DAY_STAFF && "hidden"}`}
                      onClick={(e) => editNotice(notice, e)}
                    >
                      {t("common_btn_edit")}
                    </button>
                  </div>
                  <div className="flex items-center gap-2 w-full">
                    {notice.isPrimary && (
                      <span className="badge badge-error flex-shrink-0">
                        {t("notice_important_badge")}
                      </span>
                    )}
                    <p
                      className={`flex-grow ${expandedNoticeId !== notice.id ? "line-clamp-1" : ""}`}
                    >
                      {notice.title || notice.content}
                    </p>
                  </div>
                  {expandedNoticeId !== notice.id && (
                    <p className="text-sm break-all whitespace-pre-line line-clamp-2 mt-1">
                      {notice.content}
                    </p>
                  )}
                </div>

                <div className="collapse-content">
                  <p className="text-sm break-all whitespace-pre-line">
                    {notice.content}
                  </p>
                  {notice.images.length > 0 && (
                    <NoticeImageCarousel
                      images={notice.images}
                      alt={notice.title || t("notice_title")}
                    />
                  )}
                </div>
                <div
                  className={"flex justify-center py-2 cursor-pointer"}
                  role={"button"}
                  tabIndex={0}
                  onClick={() => handleNoticeClick(notice.id!)}
                >
                  <span className={"text-sm mr-3"}>
                    {t(
                      expandedNoticeId === notice.id
                        ? "userdetail_btn_unfold"
                        : "userdetail_btn_fold",
                    )}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className={`size-5 transition-transform duration-300 ${
                      expandedNoticeId === notice.id ? "scale-y-[-1]" : ""
                    }`}
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button
        className={
          "btn-primary btn-circle absolute bottom-5 right-5 pointer-events-auto"
        }
        onClick={() => refetch()}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 16 16"
          fill="currentColor"
          className="size-4"
        >
          <path
            fillRule="evenodd"
            d="M13.836 2.477a.75.75 0 0 1 .75.75v3.182a.75.75 0 0 1-.75.75h-3.182a.75.75 0 0 1 0-1.5h1.37l-.84-.841a4.5 4.5 0 0 0-7.08.932.75.75 0 0 1-1.3-.75 6 6 0 0 1 9.44-1.242l.842.84V3.227a.75.75 0 0 1 .75-.75Zm-.911 7.5A.75.75 0 0 1 13.199 11a6 6 0 0 1-9.44 1.241l-.84-.84v1.371a.75.75 0 0 1-1.5 0V9.591a.75.75 0 0 1 .75-.75H5.35a.75.75 0 0 1 0 1.5H3.98l.841.841a4.5 4.5 0 0 0 7.08-.932.75.75 0 0 1 1.025-.273Z"
            clipRule="evenodd"
          />
        </svg>
      </Button>
      {!isBackstageAdmin && (
        <BottomModal show={showEditor}>
          <NoticeEditorComp
            initialNotice={editingNotice}
            onSave={onEditorClosed}
            onCancel={onEditorClosed}
            artistId={artistIdNum}
            eventId={eventIdNum}
            rewardId={rewardIdNum}
          />
        </BottomModal>
      )}
    </div>
  );
}
